import { Backdrop, BackdropProps, Box, Typography } from '@mui/material'
import { Button, Modal } from 'eezy-components'
import { captureError } from 'helpers/error'
import { useSnackbar } from 'notistack'
import { useEffect, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'

import { useGetApiV1UserProfileId, usePatchApiV1UserProfileId } from 'api'
import { Session } from 'types'

import * as S from './styled'

type Props = {
  session: Session
}

/**
 * Interpolated translations:
 *
 * i18n-tasks-use t('application_approved.modal.content')
 */
const ApplicationApprovedModal = ({ session }: Props): JSX.Element => {
  const { t } = useTranslation()
  const [isOpen, setOpen] = useState(true)
  const [isRenderable, setRenderable] = useState(false)
  const [isGifLoaded, setGifLoaded] = useState(false)
  const { enqueueSnackbar } = useSnackbar()

  const gifSrc = '/review-session/confetti.gif'

  const { data: fetchedUser, loading: fetchedUserLoading } =
    useGetApiV1UserProfileId({
      id: session?.id || '',
      lazy: !session?.id,
    })
  const { mutate: saveProfile, loading: savingProfile } =
    usePatchApiV1UserProfileId({
      id: session?.id || '',
    })

  useEffect(() => {
    if (isOpen) {
      const img = new Image()
      img.src = gifSrc
      img.onload = () => {
        setGifLoaded(true)
      }
      img.onerror = () => {
        console.error('Failed to load confetti GIF.')
        setGifLoaded(true)
      }
    }
  }, [isOpen, gifSrc])

  useEffect(() => {
    if (isOpen && isGifLoaded) {
      setRenderable(true)
    }
  }, [isOpen, isGifLoaded])

  useEffect(() => {
    if (fetchedUser && fetchedUser.meta_data?.recently_approved !== false) {
      saveProfile({
        user: {
          meta_data: {
            ...fetchedUser?.meta_data,
            recently_approved: false,
          },
        },
      }).catch(e => {
        captureError(
          e,
          'Error saving profile - ApplicationApprovedModal/index.tsx'
        )
        enqueueSnackbar(t('util.something_went_wrong'), {
          variant: 'error',
        })
      })
    }
  }, [enqueueSnackbar, fetchedUser, saveProfile, t])

  const onClose = async () => {
    window.location.reload()
    setOpen(false)
    setRenderable(false)
  }

  const portfolioUrl = (
    <a href="/portfolio/approved" target="_blank" rel="noopener noreferrer" />
  )
  const dashboardUrl = <a href="/" target="_blank" rel="noopener noreferrer" />
  const insightsUrl = (
    <a href="/insights/vecteezy" target="_blank" rel="noopener noreferrer" />
  )
  const supportUrl = (
    <a
      href="https://eezycontributors.zendesk.com/hc/en-us/requests/new"
      target="_blank"
      rel="noopener noreferrer"
    />
  )

  const CustomBackdrop = (props: BackdropProps) => (
    <>
      <Backdrop
        {...props}
        open={isRenderable}
        style={{
          zIndex: 1,
          opacity: 1,
          transition: 'opacity 1000ms ease-in-out',
        }}
      />
      {isRenderable && (
        <Box
          sx={{
            position: 'fixed',
            zIndex: 2,
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            opacity: 1,
            transition: 'opacity 1500ms ease-in-out',
          }}
        >
          <S.Image src={gifSrc} alt="Confetti celebration" />
        </Box>
      )}
    </>
  )

  if (!isRenderable) {
    return <></>
  }

  return (
    <Modal
      size="larger"
      modalOpen={isRenderable}
      setModalOpen={onClose}
      handleClose={onClose}
      slots={{
        backdrop: CustomBackdrop,
      }}
      PaperProps={{
        sx: {
          zIndex: 3,
          opacity: isRenderable ? 1 : 0,
          visibility: isRenderable ? 'visible' : 'hidden',
          transition: 'opacity 200ms ease-in-out, visibility 200ms ease-in-out',
        },
      }}
    >
      <Modal.Header>{t('application_approved.modal.title')}</Modal.Header>
      <Modal.Body>
        <Typography variant="body1">
          <Trans
            i18nKey="application_approved.modal.content"
            components={{
              portfolioUrl,
              dashboardUrl,
              insightsUrl,
              supportUrl,
            }}
          />
        </Typography>
      </Modal.Body>
      <Modal.Actions>
        <Button
          color="primary"
          onClick={onClose}
          disabled={fetchedUserLoading || savingProfile}
        >
          {t('close_window')}
        </Button>
      </Modal.Actions>
    </Modal>
  )
}

export default ApplicationApprovedModal
